import { __awaiter, __decorate, __extends, __generator, __metadata } from "tslib";
import React from 'react';
import { makeObservable, observable, runInAction } from 'mobx';
import { Misc } from '@singularsystems/neo-core';
import { ViewParameterList } from '../Routing/ViewParameterList';
import { ViewState } from './ViewState';
import { BreadCrumbItem } from '../Routing/BreadCrumbItem';
import { NeoReactTypes } from '../Modules/Types';
import ViewModelBase from './ViewModelBase';
var ViewBase = /** @class */ (function (_super) {
    __extends(ViewBase, _super);
    /**
     * Creates a view
     * @param props React props.
     * @param viewName Name of the view.
     * @param viewModel Instance of view model, or view model type, or callback which returns a view model.
     */
    function ViewBase(viewName, viewModel, props) {
        var _this = _super.call(this, props) || this;
        _this.viewName = "";
        /** Indicates that the view requests to be displayed full screen. Up to the main layout to accommodate this. */
        _this.fullScreen = false;
        _this.routingState = Misc.Globals.appService.get(NeoReactTypes.Routing.GlobalRoutingState);
        _this.navigation = Misc.Globals.appService.get(NeoReactTypes.Routing.NavigationHelper);
        if (typeof viewName !== "string") {
            throw "Error: When inheriting ViewBase, you must pass the view name into the base constructor.";
        }
        _this.viewName = viewName;
        _this.viewState = _this.getStoredViewState(viewModel);
        _this.routingState.currentView = _this;
        makeObservable(_this);
        return _this;
    }
    Object.defineProperty(ViewBase, "currentView", {
        // For backwards compatibility
        get: function () {
            return Misc.Globals.appService.get(NeoReactTypes.Routing.GlobalRoutingState).currentView;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(ViewBase.prototype, "viewModel", {
        get: function () {
            return this.viewState.viewModel;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(ViewBase.prototype, "taskRunner", {
        get: function () {
            return this.viewModel.taskRunner;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(ViewBase.prototype, "viewParams", {
        get: function () {
            return this.viewState.viewParams;
        },
        enumerable: false,
        configurable: true
    });
    ViewBase.prototype.getStoredViewState = function (viewModel) {
        return this.createViewState(viewModel);
    };
    ViewBase.prototype.createViewState = function (viewModel) {
        var params = this.constructor.params;
        var paramsList = new ViewParameterList(params, this);
        var viewModelInstance = ViewModelBase.create(viewModel);
        return new ViewState(paramsList, viewModelInstance);
    };
    ViewBase.prototype.componentDidMount = function () {
        var _this = this;
        var paramsUpdated = this.navigation.currentRouteProps ? this.viewState.viewParams.update(this.navigation.currentRouteProps.match) : false;
        var initPromise = undefined;
        if (!this.viewState.hasSetup) {
            this.viewState.hasSetup = true;
            initPromise = this.initialise();
        }
        if (paramsUpdated) {
            if (initPromise) {
                initPromise.then(function () { return _this.viewParamsUpdated(); });
            }
            else {
                this.viewParamsUpdated();
            }
        }
    };
    ViewBase.prototype.componentWillUnmount = function () {
        this.disposeViewModel();
    };
    ViewBase.prototype.updateViewParams = function (match) {
        var _this = this;
        runInAction(function () { return __awaiter(_this, void 0, void 0, function () {
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        if (!this.viewState.viewParams.update(match)) return [3 /*break*/, 2];
                        return [4 /*yield*/, Promise.resolve(this.viewParamsUpdated())];
                    case 1:
                        _a.sent();
                        _a.label = 2;
                    case 2: return [2 /*return*/];
                }
            });
        }); });
    };
    /**
     * viewParamsUpdated() is called when the component is mounted, and whenever a route parameter or query parameter gets a changed value.
     */
    ViewBase.prototype.viewParamsUpdated = function () {
    };
    /** Gets the parent breadcrumbs, and the breadcrumb for this view. */
    ViewBase.prototype.getBreadCrumbStart = function () {
        var _this = this;
        var items = [];
        // Get parent items
        var menuRoute = this.routingState.currentRoute;
        var parent = menuRoute.breadCrumbParent;
        while (parent) {
            items.splice(0, 0, new BreadCrumbItem(parent.name, parent.path));
            parent = parent.breadCrumbParent;
        }
        // Add view name
        var firstParam = this.viewState.viewParams.params[0];
        items.push(new BreadCrumbItem(function () { return _this.viewName; }, (firstParam === null || firstParam === void 0 ? void 0 : firstParam.routeParameter.required) ? "" : this.navigation.getCurrentViewPath()));
        return items;
    };
    /**
     * Initialise is called from componentDidMount().
     * Will only be called once per persistent view for the lifetime of the app.
     * If overridden, you must call super.initialise() to initialise the ViewModel.
     */
    ViewBase.prototype.initialise = function () {
        return this.viewModel.initialise();
    };
    ViewBase.prototype.disposeViewModel = function () {
        this.viewModel.dispose();
    };
    ViewBase.prototype.getBreadCrumbList = function () {
        var breadCrumbList = [];
        if (!this.breadCrumbStartItems) {
            this.breadCrumbStartItems = this.getBreadCrumbStart();
        }
        breadCrumbList.push.apply(breadCrumbList, this.breadCrumbStartItems);
        breadCrumbList.push.apply(breadCrumbList, this.viewParams.getBreadCrumbList());
        return breadCrumbList;
    };
    /**
     * Called when the user navigates to a new view, or when exiting the site.
     * Return a string to show to the user in a dialog.
     */
    ViewBase.prototype.onLeave = function () {
        return undefined;
    };
    /**
     * Called whenever the route changes. Including navigating to a new view, leaving the site, or changing a route parameter in this view.
     * Note: if props.leavePrompt is set, onLeave() will not be called.
     * @param props
     */
    ViewBase.prototype.beforeRouteChanged = function (props) {
    };
    __decorate([
        observable.ref,
        __metadata("design:type", String)
    ], ViewBase.prototype, "viewName", void 0);
    __decorate([
        observable.ref,
        __metadata("design:type", Boolean)
    ], ViewBase.prototype, "fullScreen", void 0);
    return ViewBase;
}(React.Component));
export default ViewBase;
